import {
  ExpandCollapsePanel,
  Icon,
  Panel,
  PanelContent,
  PanelTrigger,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type SelectOption
} from '@deque/cauldron-react';
import dayjs from 'dayjs';
import React, { type ComponentProps, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { type AdminFeatureFlag, type SSOConfig } from '~/common/api-client';

import type { OrNull } from '~/typings/utils';
import UserIdLink from '../containers/UserIdLink';
import useSorter from '../hooks/useSorter';
import styles from './FeatureFlagsTableAdmin.css';

export type HeaderKeys =
  | 'id'
  | 'code_state'
  | 'db_override'
  | 'product_name'
  | 'created_at'
  | 'updated_by'
  | 'updated_at'
  | 'db_overridden_created_at'
  | 'db_overridden_created_by';

type HeaderType = {
  key: HeaderKeys;
  title: string;
};

export const tableHeaders: HeaderType[] = [
  {
    key: 'id',
    title: 'ID'
  },
  {
    key: 'code_state',
    title: 'Code state'
  },
  {
    key: 'db_override',
    title: 'DB override'
  },
  {
    key: 'db_overridden_created_at',
    title: 'DB override created at'
  },
  {
    key: 'db_overridden_created_by',
    title: 'DB override created by'
  },
  {
    key: 'created_at',
    title: 'Created at'
  },
  {
    key: 'updated_at',
    title: 'Updated at'
  },
  {
    key: 'updated_by',
    title: 'Updated by'
  },
  { key: 'product_name', title: 'Product' }
];

type TimestampProps = {
  date: Date;
};

const Timestamp: React.FC<TimestampProps> = ({ date }) => {
  const dateTime = dayjs(date).toDate().toISOString();
  const formatted = React.useMemo(
    () => new Intl.DateTimeFormat('en').format(dayjs(date).toDate()),
    [date]
  );

  return (
    <time dateTime={dateTime} data-testid="timestamp">
      {formatted}
    </time>
  );
};

export enum FeatureFlagState {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  NONE = 'none'
}

export interface OnStateChangePayload {
  id: string;
  newState: FeatureFlagState;
}

interface OverrideStateSelectProps {
  onChange: (payload: OnStateChangePayload) => void;
  state: boolean;
  overridden: boolean;
  id: string;
  disabled?: boolean;
}

const FeatureStateSelect: FC<OverrideStateSelectProps> = ({
  state,
  overridden,
  id,
  disabled = false,
  onChange
}) => {
  const { t } = useTranslation();

  const handleSelect = (value: string) => {
    if (Object.values(FeatureFlagState).includes(value as FeatureFlagState)) {
      onChange({
        id,
        newState: value as FeatureFlagState
      });
    }
  };

  const selectOptions: SelectOption[] = React.useMemo(() => {
    const options = {
      [FeatureFlagState.ENABLED]: t('Enabled'),
      [FeatureFlagState.DISABLED]: t('Disabled'),
      [FeatureFlagState.NONE]: t('None')
    };
    return Object.entries(options).map(([value, label]) => ({
      key: value,
      label,
      value
    }));
  }, [t]);

  const getSelectedState = () => {
    if (!overridden) {
      return FeatureFlagState.NONE;
    }
    return state ? FeatureFlagState.ENABLED : FeatureFlagState.DISABLED;
  };

  const selected = getSelectedState();

  return (
    <div className={styles.stateSelectContainer}>
      <Select
        disabled={disabled}
        label={''}
        aria-label={t('Feature {{id}} override state', { id })}
        value={selected}
        onChange={e => handleSelect(e.target.value)}
        options={selectOptions}
      />
    </div>
  );
};

export type FeatureFlagTableAdminProps = {
  featureFlags: AdminFeatureFlag[];
  isUpdatingDisabled?: boolean;
  isNarrow?: boolean;
  ssoConfig?: OrNull<SSOConfig>;
  onFeatureStateChange: (payload: OnStateChangePayload) => void;
};

const FeatureFlagTableAdmin: FC<FeatureFlagTableAdminProps> = ({
  featureFlags,
  isUpdatingDisabled = false,
  isNarrow,
  ssoConfig,
  onFeatureStateChange
}) => {
  const { t } = useTranslation();

  const dbOverrideSortValue = (feature: AdminFeatureFlag) => {
    if (!feature.overridden) {
      return -2; // none;
    }
    return Number(feature.default_state);
  };

  const sorter = useSorter<AdminFeatureFlag, HeaderKeys>({
    items: featureFlags,
    initialSortDirection: 'ascending',
    initialSortBy: 'id',
    sortHandlers: {
      id: (a, b) => {
        return a.id.localeCompare(b.id);
      },
      code_state: (a, b) => {
        return Number(a.initial_state) - Number(b.initial_state);
      },
      db_override: (a, b) => {
        return dbOverrideSortValue(a) - dbOverrideSortValue(b);
      },
      product_name: (a, b) => {
        const leftHand = a.product_name.toLowerCase();
        const rightHand = b.product_name.toLowerCase();

        return leftHand.localeCompare(rightHand);
      },
      created_at: (a, b) => {
        const leftHand = new Date(a.created_at).getTime();
        const rightHand = new Date(b.created_at).getTime();

        return leftHand - rightHand;
      },
      updated_by: (a, b) => {
        const leftHand = a.updated_by || '';
        const rightHand = b.updated_by || '';

        return leftHand.localeCompare(rightHand);
      },
      updated_at: (a, b) => {
        const leftHand = a.updated_at
          ? new Date(a.updated_at).getTime()
          : Infinity;
        const rightHand = b.updated_at
          ? new Date(b.updated_at).getTime()
          : Infinity;
        return leftHand - rightHand;
      },
      db_overridden_created_at: (a, b) => {
        const leftHand = a.db_overridden_created_at
          ? new Date(a.db_overridden_created_at).getTime()
          : Infinity;
        const rightHand = b.db_overridden_created_at
          ? new Date(b.db_overridden_created_at).getTime()
          : Infinity;
        return leftHand - rightHand;
      },
      db_overridden_created_by: (a, b) => {
        const leftHand = a.db_overridden_created_by || '';
        const rightHand = b.db_overridden_created_by || '';
        return leftHand.localeCompare(rightHand);
      }
    }
  });

  const getSortingPropsForTableHeader = (
    sortingPropName: HeaderKeys
  ): Pick<ComponentProps<typeof TableHeader>, 'onSort' | 'sortDirection'> => {
    return {
      sortDirection:
        sorter.sortBy === sortingPropName ? sorter.sortDirection : 'none',
      onSort: () => sorter.updateSortBy(sortingPropName)
    };
  };

  return (
    <>
      {!isNarrow ? (
        <Table className={styles.featureFlagsTable}>
          <TableHead>
            <TableRow>
              {tableHeaders.map(({ key, title }) => {
                return (
                  <TableHeader
                    key={key}
                    {...getSortingPropsForTableHeader(key)}
                  >
                    {title}
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorter.sortedItems.map(feature => {
              return (
                <TableRow key={feature.id}>
                  <TableHeader scope="row">
                    {!feature.description ? (
                      <div>{feature.id}</div>
                    ) : (
                      <ExpandCollapsePanel animationTiming={0}>
                        <PanelTrigger
                          className={styles.featureDescriptionTrigger}
                        >
                          {feature.id}
                        </PanelTrigger>
                        <em>{feature.description}</em>
                      </ExpandCollapsePanel>
                    )}
                  </TableHeader>
                  <TableCell className={styles.codeStateCell}>
                    {feature.initial_state ? t('Enabled') : t('Disabled')}
                  </TableCell>
                  <TableCell>
                    <FeatureStateSelect
                      disabled={isUpdatingDisabled}
                      overridden={feature.overridden}
                      id={feature.id}
                      state={feature.default_state}
                      onChange={onFeatureStateChange}
                    />
                  </TableCell>
                  <TableCell>
                    {feature.db_overridden_created_at ? (
                      <Timestamp date={feature.db_overridden_created_at} />
                    ) : (
                      <Icon type="no" label={t('No DB override value')} />
                    )}
                  </TableCell>
                  <TableCell>
                    {feature.db_overridden_created_by ? (
                      <UserIdLink
                        id={feature.db_overridden_created_by}
                        ssoConfig={ssoConfig}
                      />
                    ) : (
                      <Icon type="no" label={t('No DB override value')} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Timestamp date={feature.created_at} />
                  </TableCell>
                  <TableCell>
                    {feature.updated_at ? (
                      <Timestamp date={feature.updated_at} />
                    ) : (
                      <Icon type="no" label={t('No updated_at value')} />
                    )}
                  </TableCell>
                  <TableCell>
                    {feature.updated_by ? (
                      <UserIdLink
                        id={feature.updated_by}
                        ssoConfig={ssoConfig}
                      />
                    ) : (
                      <Icon type="no" label={t('No updated_by value')} />
                    )}
                  </TableCell>
                  <TableCell>{feature.product_name}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Panel padding={false}>
          <ul className={styles.mobileFeatureList}>
            {sorter.sortedItems.map(feature => {
              return (
                <li key={feature.id}>
                  <PanelContent>
                    <h2>{t('ID')}</h2>
                    <p>{feature.id}</p>

                    <h2>{t('Description')}</h2>
                    <p>{feature.description}</p>

                    <h2>{t('Code state')}</h2>
                    <p>
                      {feature.initial_state ? t('Enabled') : t('Disabled')}
                    </p>

                    <h2>{t('DB override')}</h2>
                    <FeatureStateSelect
                      disabled={isUpdatingDisabled}
                      overridden={feature.overridden}
                      id={feature.id}
                      state={feature.default_state}
                      onChange={onFeatureStateChange}
                    />

                    <h2>{t('DB override created at')}</h2>
                    <p>
                      {feature.db_overridden_created_at ? (
                        <Timestamp date={feature.db_overridden_created_at} />
                      ) : (
                        <Icon type="no" label={t('No DB override value')} />
                      )}
                    </p>

                    <h2>{t('DB override created by')}</h2>
                    <p>
                      {feature.db_overridden_created_by ? (
                        <UserIdLink
                          id={feature.db_overridden_created_by}
                          ssoConfig={ssoConfig}
                        />
                      ) : (
                        <Icon type="no" label={t('No DB override value')} />
                      )}
                    </p>

                    <h2>{t('Created at')}</h2>
                    <p>
                      <Timestamp date={feature.created_at} />
                    </p>

                    <h2>{t('Updated at')}</h2>
                    <p>
                      {feature.updated_at ? (
                        <Timestamp date={feature.updated_at} />
                      ) : (
                        <Icon type="no" label={t('No updated_at value')} />
                      )}
                    </p>

                    <h2>{t('Updated by')}</h2>
                    <p>
                      {feature.updated_by ? (
                        <UserIdLink
                          id={feature.updated_by}
                          ssoConfig={ssoConfig}
                        />
                      ) : (
                        <Icon type="no" label={t('No updated_by value')} />
                      )}
                    </p>

                    <h2>{t('Product')}</h2>
                    <p>{feature.product_name}</p>
                  </PanelContent>
                </li>
              );
            })}
          </ul>
        </Panel>
      )}
    </>
  );
};

export default FeatureFlagTableAdmin;
